import { AloftDate, AloftTime } from '@aloft/shared-objects';
import { Block, ButtonProps, Flex, Link, Typography, useModal } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import SamWave from '../../assets/sam/wave.png';
import { fetchGetBlob } from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { useTkv2PDF } from '../../modules/tkv2PDF/utils/useTkv2PDF.tsx';
import { selectAccount } from '../../redux/features/selectors/accountSelectors.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { LoadingScreenSam } from '../LoadingScreen/LoadingScreenSam.tsx';
import { useCertifyModalForm } from './hooks/useCertifyModalForm.ts';
import { useCertifyModalSubmission } from './hooks/useCertifyModalSubmission.tsx';
import { CertifyModalFormOutput } from './schemas.ts';
import { CertifyModalAcknowledgment } from './sections/CertifyModalAcknowledgment.tsx';
import { CertifyModalCommentary } from './sections/CertifyModalCommentary.tsx';
import { CertifyModalLicenseDetails } from './sections/CertifyModalLicenseDetails.tsx';
import { CertifyModalSignature } from './sections/CertifyModalSignature.tsx';

export const CertifyModalInternal = ({
  Modal,
  closeModal,
  isModalOpen,
}: {
  Modal: ReturnType<typeof useModal>['Modal'];
  closeModal: ReturnType<typeof useModal>['closeModal'];
  isModalOpen: ReturnType<typeof useModal>['isModalOpen'];
}) => {
  const [hasAccountSignature, setHasAccountSignature] = useState(false);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);

  const { fetcherOptions } = useToolkitContext();
  const form = useCertifyModalForm();
  const commentary = useAppSelector(selectCommentary);
  const { signatureBlobId } = useAppSelector(selectAccount);
  const tkv2PDFData = useTkv2PDF();
  const { id, isMock, signedAt } = useAppSelector(selectOrder);

  const signatureDate = signedAt
    ? AloftTime.schema.parse(signedAt).toAloftDate().format('MM/dd/yyyy')
    : AloftDate.today().format('MM/dd/yyyy');

  const { submitModal, isSubmitting, isCompleted, isError, submissionText } =
    useCertifyModalSubmission({
      orderID: id,
      tkv2PDFData,
    });

  const navigate = useNavigate();

  useEffect(() => {
    if (!commentary) return;
    form.reset({
      certificationCommentary: commentary?.reconciliation?.certificationCommentary,
      saveSignature: true,
    });
  }, [commentary]);

  useEffect(() => {
    if (signatureBlobId) setHasAccountSignature(true);
  }, [signatureBlobId]);

  const handleUseAccountSignature = () => {
    form.setValue('useAccountSignature', true);
    if (signatureBlobId) {
      fetchGetBlob({ ...fetcherOptions, pathParams: { blobID: signatureBlobId } })
        .then((blob) => {
          const file = new File([blob as BlobPart], 'signature.png', { type: blob.type });
          form.setValue('signatureFile', file);
          setSubmitButtonEnabled(true);
        })
        .catch(console.error);
    }
  };

  form.subscribe({
    name: 'signatureFile',
    callback: (data) => {
      if (data.values?.signatureFile && !submitButtonEnabled) {
        setSubmitButtonEnabled(true);
      } else if (!data.values?.signatureFile && submitButtonEnabled) {
        setSubmitButtonEnabled(false);
      }
    },
  });

  let primaryAction: ButtonProps | undefined;
  if (isCompleted) {
    primaryAction = {
      text: 'Return to My Orders',
      onClick: () => {
        navigate('/completed-orders');
      },
      disabled: false,
    };
  } else if (!isSubmitting && !isError) {
    primaryAction = {
      text: isMock ? 'Submit' : 'Sign Report',
      onClick: () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        submitModal(form as UseFormReturn<CertifyModalFormOutput>);
      },
      icon: isMock ? undefined : 'signature',
      disabled: !submitButtonEnabled && !isMock && !isSubmitting,
    };
  }

  return (
    <Modal
      closeOnEsc={false}
      isOpen={isModalOpen}
      onClose={closeModal}
      primaryAction={primaryAction}
      secondaryAction={
        isSubmitting || isCompleted
          ? undefined
          : {
              text: isError ? 'Okay' : 'Close',
              onClick: closeModal,
            }
      }
      title={isMock ? 'Complete Demo Appraisal' : 'Appraisal Certification'}
      showCloseButton={false}
      size="xl"
    >
      {(() => {
        if (isCompleted) {
          return (
            <Flex flexDirection="column" gap={2}>
              <Flex justifyContent="center">
                <img src={SamWave} width="100px" alt="sam" />
              </Flex>
              <Typography variant="h2">
                Great job! Your appraisal order has been successfully completed.
              </Typography>
              <Typography variant="body1">
                You can download a PDF version of the report from the My Orders page by navigating
                to the &apos;Completed&apos; tab.
              </Typography>
              <Typography variant="h4" color="textSecondary">
                Report Date: {signatureDate}
              </Typography>
            </Flex>
          );
        }
        if (isSubmitting && !isError) {
          return (
            <Flex justifyContent="center" alignItems="center">
              <Block width="300px" height="300px">
                <LoadingScreenSam overrideText={submissionText} />
              </Block>
            </Flex>
          );
        }
        if (isError) {
          return (
            <Flex flexDirection="column" gap={2}>
              <Typography variant="h2">Unable to sign report</Typography>
              <Typography variant="body1">
                Please contact&nbsp;
                <Link href="mailto:orders@appraisify.com" color="buttonDefault">
                  orders@appraisify.com
                </Link>
                .
              </Typography>
            </Flex>
          );
        }
        return isMock ? (
          <Flex flexDirection="column" gap={4}>
            <Typography variant="h3">
              Are you sure you want to complete this demo appraisal?
            </Typography>
            <Typography variant="body1">
              You will still be able to view a copy of the report from the Completed tab.
            </Typography>
          </Flex>
        ) : (
          <Flex flexDirection="column" gap={4}>
            <CertifyModalLicenseDetails />
            <CertifyModalCommentary form={form} />
            <CertifyModalAcknowledgment />
            <CertifyModalSignature
              form={form}
              hasAccountSignature={hasAccountSignature}
              handleUseAccountSignature={handleUseAccountSignature}
            />
          </Flex>
        );
      })()}
    </Modal>
  );
};
